import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import BabelTraverse from "@babel/traverse";
import { parseComponent } from "./parseComponent.js";
import { parseFunction } from "./parseFunction.js";
import { validateGlobalApi } from "./validations.js";
import { parseChildren } from "./parseChildren.js";
import { parseFile } from "./parseFile.js";
import { resolveImportSource } from "./resolveImportSource.js";
import { getViewTitle } from "./getViewTitle.js";
var traverse = process.env.NODE_ENV === "test" ? BabelTraverse : BabelTraverse["default"];
var FunctionScope = /*#__PURE__*/function (FunctionScope) {
  FunctionScope["TopLevelStatement"] = "top level statement";
  FunctionScope["NamedExportDeclaration"] = "named export declaration";
  FunctionScope["DefaultExportDeclaration"] = "default export declaration";
  return FunctionScope;
}(FunctionScope || {});
export function parseModule(mod, app, ast, options) {
  var functionBindings = new Set();
  var componentBindings = new Set();
  var contextBindings = new Set();
  var globalOptions = {
    functionBindings: functionBindings,
    componentBindings: componentBindings,
    contextBindings: contextBindings,
    stateBindings: options === null || options === void 0 ? void 0 : options.withContexts
  };
  traverse(ast, {
    Program: function Program(path) {
      var body = path.get("body");
      var functionNodes = [];
      var variableDeclarations = [];
      var defaultExportNode = null;
      var renderNode = null;
      var imports = [];
      var _collectFunctions = function collectFunctions(stmt, scope) {
        if (stmt.isFunctionDeclaration()) {
          var id = stmt.node.id;
          if (id) {
            if (isComponent(id, mod)) {
              componentBindings.add(id);
            } else {
              functionBindings.add(id);
            }
          }
          if (scope === FunctionScope.DefaultExportDeclaration) {
            if (app.appType === "app" && mod.moduleType === "entry") {
              mod.errors.push({
                message: "The entry module in an app cannot have a default export declaration.",
                node: stmt.node,
                severity: "error"
              });
            } else {
              defaultExportNode = stmt;
            }
            return;
          }
          if (!id) {
            mod.errors.push({
              message: "Function declaration must have a name",
              node: stmt.node,
              severity: "error"
            });
            return;
          }
          functionNodes.push({
            func: stmt,
            exported: scope === FunctionScope.NamedExportDeclaration
          });
        } else if (stmt.isVariableDeclaration()) {
          if (scope === FunctionScope.DefaultExportDeclaration) {
            mod.errors.push({
              message: "Default export declaration must be a function declaration.",
              node: stmt.node,
              severity: "error"
            });
            return;
          }
          variableDeclarations.push({
            decl: stmt,
            exported: scope === FunctionScope.NamedExportDeclaration
          });
        } else if (stmt.isExportDefaultDeclaration()) {
          var decl = stmt.get("declaration");
          _collectFunctions(decl, FunctionScope.DefaultExportDeclaration);
        } else if (stmt.isExportNamedDeclaration()) {
          var _decl = stmt.get("declaration");
          _collectFunctions(_decl, FunctionScope.NamedExportDeclaration);
        } else if (stmt.isImportDeclaration()) {
          var importSource = stmt.node.source.value;
          if (importSource.startsWith(".") || importSource.startsWith("/")) {
            imports.push(importSource);
          }
        } else if (!stmt.isTSInterfaceDeclaration && !stmt.isTSTypeAliasDeclaration()) {
          mod.errors.push({
            message: "Unsupported ".concat(scope, " type: ").concat(stmt.type),
            node: stmt.node,
            severity: "error"
          });
        }
      };
      var _iterator = _createForOfIteratorHelper(body),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var stmt = _step.value;
          if (app.appType === "app" && mod === app.entry && stmt.isExpressionStatement()) {
            var expr = stmt.get("expression");
            if (expr.isCallExpression()) {
              var _callee = expr.get("callee");
              if (_callee.isIdentifier() && validateGlobalApi(_callee, "render")) {
                var args = expr.get("arguments");
                if (args.length !== 1) {
                  mod.errors.push({
                    message: "render() expects exactly one argument, received: ".concat(args.length),
                    node: expr.node,
                    severity: "error"
                  });
                  continue;
                }
                renderNode = args[0];
                continue;
              }
            }
          }
          _collectFunctions(stmt, FunctionScope.TopLevelStatement);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      for (var _i = 0, _variableDeclarations = variableDeclarations; _i < _variableDeclarations.length; _i++) {
        var _variableDeclarations2 = _variableDeclarations[_i],
          decl = _variableDeclarations2.decl,
          exported = _variableDeclarations2.exported;
        if (decl.node.kind !== "const") {
          mod.errors.push({
            message: "Global variables must be declared with const.",
            node: decl.node,
            severity: "error"
          });
          continue;
        }
        var _iterator2 = _createForOfIteratorHelper(decl.get("declarations")),
          _step2;
        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var declarator = _step2.value;
            var init = declarator.get("init");
            if (!init.isCallExpression()) {
              mod.errors.push({
                message: "Global variables must be initialized with a call expression, but got: ".concat(init.type),
                node: init.node,
                severity: "error"
              });
              continue;
            }
            var callee = init.get("callee");
            if (!callee.isIdentifier()) {
              mod.errors.push({
                message: "Unsupported variable initializer type: ".concat(callee.type),
                node: callee.node,
                severity: "error"
              });
              continue;
            }
            if (!validateGlobalApi(callee, "createContext")) {
              mod.errors.push({
                message: "Global variables must be initialized with createContext, but got: ".concat(callee.node.name),
                node: callee.node,
                severity: "error"
              });
              continue;
            }
            var id = declarator.get("id");
            if (!id.isIdentifier()) {
              mod.errors.push({
                message: "Unsupported context identifier type: ".concat(id.type),
                node: id.node,
                severity: "error"
              });
              continue;
            }
            var part = {
              type: "context",
              context: id.node.name
            };
            contextBindings.add(id.node);
            if (exported) {
              mod.namedExports.set(id.node.name, part);
            } else {
              mod.internals.push(part);
            }
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }
      for (var _i2 = 0, _imports = imports; _i2 < _imports.length; _i2++) {
        var importSource = _imports[_i2];
        parseFile(resolveImportSource(importSource, mod.filePath, app.files), app, undefined, options);
      }
      for (var _i3 = 0, _functionNodes = functionNodes; _i3 < _functionNodes.length; _i3++) {
        var _functionNodes$_i = _functionNodes[_i3],
          item = _functionNodes$_i.func,
          _exported = _functionNodes$_i.exported;
        var _id = item.node.id;
        if (isComponent(_id, mod)) {
          var type = app.appType === "app" && mod.moduleType === "entry" ? "page" : "template";
          var component = parseComponent(item, mod, app, type, globalOptions);
          if (component) {
            var _part = {
              type: type,
              component: component
            };
            if (_exported) {
              mod.namedExports.set(_id.name, _part);
            } else {
              mod.internals.push(_part);
            }
          }
        } else {
          var func = parseFunction(item, mod, app, globalOptions);
          if (func) {
            var _part2 = {
              type: "function",
              "function": func
            };
            if (_exported) {
              mod.namedExports.set(_id.name, _part2);
            } else {
              mod.internals.push(_part2);
            }
          }
        }
      }
      if (defaultExportNode) {
        var funcId = defaultExportNode.node.id;
        if (funcId ? isComponent(funcId, mod) : mod.moduleType !== "function") {
          var _type = mod.moduleType === "entry" ? app.appType === "template" ? "template" : "view" : mod.moduleType === "page" ? "page" : "template";
          var _component = parseComponent(defaultExportNode, mod, app, _type, globalOptions);
          if (_component) {
            mod.defaultExport = _objectSpread({
              type: _type,
              component: _component
            }, _type === "view" ? {
              title: getViewTitle(_component)
            } : {});
          }
        } else {
          var _func = parseFunction(defaultExportNode, mod, app, globalOptions);
          if (_func) {
            mod.defaultExport = {
              type: "function",
              "function": _func
            };
          }
        }
      }
      if (renderNode) {
        var children = parseChildren(renderNode, mod, app, globalOptions);
        mod.render = {
          type: "render",
          children: children
        };
      }
      path.stop();
    }
  });
}

/**
 * Checks if a given identifier is a component.
 *
 * For a module of type "function", no components are allowed.
 *
 * Convention: components have names starting with uppercase letter
 * e.g. MyComponent, HelloWorld
 * functions have names starting with lowercase letter
 * e.g. myFunction, helloWorld
 */
function isComponent(id, mod) {
  return mod.moduleType !== "function" && id.name[0] >= "A" && id.name[0] <= "Z";
}
//# sourceMappingURL=parseModule.js.map